import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({ baseURL: API });

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (window.location.pathname !== "/login" && !window.location.pathname.startsWith("/menu/")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

const api = {
  auth: {
    sendMagicLink: (email) => apiClient.post("/auth/send-magic-link", { email }),
    verifyOtp: (data) => apiClient.post("/auth/verify-otp", data),
    me: () => apiClient.get("/auth/me"),
  },
  admin: {
    stats: () => apiClient.get("/admin/stats"),
    getRestaurants: () => apiClient.get("/admin/restaurants"),
    createRestaurant: (data) => apiClient.post("/admin/restaurants", data),
    updateRestaurant: (id, data) => apiClient.put(`/admin/restaurants/${id}`, data),
    getOverridesCount: (id) => apiClient.get(`/admin/restaurants/${id}/overrides-count`),
    deleteOverrides: (id, menuType) => apiClient.delete(`/admin/restaurants/${id}/overrides`, { params: { menu_type: menuType } }),
    deleteRestaurant: (id) => apiClient.delete(`/admin/restaurants/${id}`),
    restaurantImportPreview: (formData) => apiClient.post("/admin/restaurants/import-preview", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    restaurantImportApply: (data) => apiClient.post("/admin/restaurants/import-apply", data),
    // Standard Menus (multiple)
    getStandardMenus: () => apiClient.get("/admin/standard-menus"),
    createStandardMenu: (data) => apiClient.post("/admin/standard-menus", data),
    updateStandardMenu: (id, data) => apiClient.put(`/admin/standard-menus/${id}`, data),
    deleteStandardMenu: (id, force) => apiClient.delete(`/admin/standard-menus/${id}`, { params: { force } }),
    getMenuContent: (mid) => apiClient.get(`/admin/standard-menus/${mid}/content`),
    createCategory: (mid, data) => apiClient.post(`/admin/standard-menus/${mid}/categories`, data),
    updateCategory: (mid, id, data) => apiClient.put(`/admin/standard-menus/${mid}/categories/${id}`, data),
    deleteCategory: (mid, id) => apiClient.delete(`/admin/standard-menus/${mid}/categories/${id}`),
    createItem: (mid, data) => apiClient.post(`/admin/standard-menus/${mid}/items`, data),
    updateItem: (mid, id, data) => apiClient.put(`/admin/standard-menus/${mid}/items/${id}`, data),
    deleteItem: (mid, id) => apiClient.delete(`/admin/standard-menus/${mid}/items/${id}`),
    reorderMenu: (mid, data) => apiClient.put(`/admin/standard-menus/${mid}/reorder`, data),
    // Products
    getProducts: () => apiClient.get("/admin/products"),
    createProduct: (data) => apiClient.post("/admin/products", data),
    updateProduct: (id, data) => apiClient.put(`/admin/products/${id}`, data),
    deleteProduct: (id, force) => apiClient.delete(`/admin/products/${id}`, { params: { force } }),
    getProductMenus: (id) => apiClient.get(`/admin/products/${id}/menus`),
    updateProductMenuPrice: (pid, mpid, data) => apiClient.put(`/admin/products/${pid}/menu-price/${mpid}`, data),
    batchUpdateMenuPrices: (pid, items) => apiClient.put(`/admin/products/${pid}/menu-prices-batch`, { items }),
    bulkImportProducts: (formData) => apiClient.post("/admin/products/bulk-import-products", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    bulkApplyProducts: (data) => apiClient.post("/admin/products/bulk-apply-products", data),
    applySingleProduct: (data) => apiClient.post("/admin/products/apply-single", data),
    // Menu product assignments
    assignProduct: (mid, data) => apiClient.post(`/admin/standard-menus/${mid}/assign-product`, data),
    updateMenuProduct: (mid, mpid, data) => apiClient.put(`/admin/standard-menus/${mid}/menu-products/${mpid}`, data),
    removeMenuProduct: (mid, mpid) => apiClient.delete(`/admin/standard-menus/${mid}/menu-products/${mpid}`),
    menuImportPreview: (mid, formData) => apiClient.post(`/admin/standard-menus/${mid}/import-preview`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    menuImportApply: (mid, data) => apiClient.post(`/admin/standard-menus/${mid}/import-apply`, data),
    // Legacy
    getStandardMenu: () => apiClient.get("/admin/standard-menu"),
    getUsers: (params) => apiClient.get("/admin/users", { params }),
    createUser: (data) => apiClient.post("/admin/users", data),
    toggleUserActive: (id) => apiClient.put(`/admin/users/${id}/toggle-active`),
    deleteUser: (id) => apiClient.delete(`/admin/users/${id}`),
    impersonate: (userId) => apiClient.post(`/admin/impersonate/${userId}`),
    updateUserRestaurants: (userId, restaurant_ids) => apiClient.put(`/admin/users/${userId}/restaurants`, { restaurant_ids }),
    getSmtp: () => apiClient.get("/admin/smtp"),
    updateSmtp: (data) => apiClient.put("/admin/smtp", data),
    testSmtp: () => apiClient.post("/admin/smtp/test"),
    getPriceLogs: (params) => apiClient.get("/admin/price-logs", { params }),
  },
  restaurant: {
    myRestaurants: () => apiClient.get("/restaurant/my-restaurants"),
    getProfile: (rid) => apiClient.get("/restaurant/profile", { params: rid ? { rid } : {} }),
    updateProfile: (data, rid) => apiClient.put("/restaurant/profile", data, { params: rid ? { rid } : {} }),
    getMenu: (rid, menuType) => apiClient.get("/restaurant/menu", { params: { ...(rid ? { rid } : {}), menu_type: menuType || "main" } }),
    updateOverride: (data, rid, menuType) => apiClient.put("/restaurant/menu/override", data, { params: { ...(rid ? { rid } : {}), menu_type: menuType || "main" } }),
    reorderMenu: (data, rid, menuType) => apiClient.put("/restaurant/menu/reorder", data, { params: { ...(rid ? { rid } : {}), menu_type: menuType || "main" } }),
    getNotifications: (rid) => apiClient.get("/restaurant/notifications", { params: rid ? { rid } : {} }),
    createNotification: (data, rid) => apiClient.post("/restaurant/notifications", data, { params: rid ? { rid } : {} }),
    updateNotification: (id, data, rid) => apiClient.put(`/restaurant/notifications/${id}`, data, { params: rid ? { rid } : {} }),
    deleteNotification: (id, rid) => apiClient.delete(`/restaurant/notifications/${id}`, { params: rid ? { rid } : {} }),
  },
  public: {
    getMenu: (slug) => apiClient.get(`/public/menu/${slug}`),
    getNotifications: (slug) => apiClient.get(`/public/notifications/${slug}`),
  },
  templates: {
    list: () => apiClient.get("/templates"),
  },
  upload: (formData) =>
    apiClient.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }),
};

export default api;
